import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CustomNavbar from '../CustomPermissions/CustomNavbar';
import { API_BASE_URL } from '../../config/api';

const AuditReport = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [auditProfile, setAuditProfile] = useState({
        desc: '',
        doc_file: null,
        excel_file: null,
        user: {}
    });

    // State for new files to upload
    const [uploadFiles, setUploadFiles] = useState({
        doc_files: [],
        excel_files: [],
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        fetchAuditProfile();
    }, [userId]);

    const fetchAuditProfile = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/Audit/profile/${userId}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            if (response.data) {
                setAuditProfile(response.data);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching audit profile:', error);
            setMessage({ text: 'Error loading audit profile.', type: 'danger' });
            setIsLoading(false);
        }
    };

    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        setAuditProfile({ ...auditProfile, desc: data });
    };

    const handleFileChange = (e, fieldName) => {
        if (e.target.files && e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files);
            setUploadFiles({ ...uploadFiles, [fieldName]: [...uploadFiles[fieldName], ...filesArray] });
        }
    };

    const removeNewFile = (fieldName, index) => {
        const updatedFiles = [...uploadFiles[fieldName]];
        updatedFiles.splice(index, 1);
        setUploadFiles({ ...uploadFiles, [fieldName]: updatedFiles });
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        
        setIsDeleting(true);
        try {
            await axios.delete(`${API_BASE_URL}/Audit/file/${fileId}/delete/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            setMessage({ text: 'File deleted successfully!', type: 'success' });
            fetchAuditProfile();
        } catch (error) {
            console.error('Error deleting file:', error);
            setMessage({ text: 'Failed to delete file.', type: 'danger' });
        }
        setIsDeleting(false);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setMessage({ text: '', type: '' });

        const formData = new FormData();
        formData.append('desc', auditProfile.desc || '');

        // Append multiple files
        uploadFiles.doc_files.forEach(file => {
            formData.append('doc_files', file);
        });
        uploadFiles.excel_files.forEach(file => {
            formData.append('excel_files', file);
        });

        try {
            const response = await axios.put(`${API_BASE_URL}/Audit/profile/${userId}/update/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });

            setMessage({ text: 'Audit profile saved successfully!', type: 'success' });
            if (response.data && response.data.data) {
                setAuditProfile(response.data.data);
                setUploadFiles({
                    doc_files: [],
                    excel_files: [],
                });
                // Reset DOM file inputs
                const fileInputs = document.querySelectorAll('input[type="file"]');
                fileInputs.forEach(input => input.value = '');
            }
        } catch (error) {
            console.error('Error saving audit profile:', error);
            setMessage({ text: 'Failed to save audit profile. Please try again.', type: 'danger' });
        }
        setIsSaving(false);
    };

    const removeMediaPrefix = (url) => {
        if (!url) return '';
        return (API_BASE_URL.replace('/api', '') + url).replace('//media', '/media')
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    if (isLoading) {
        return (
            <div className="d-flex flex-column flex-md-row">
                <CustomNavbar />
                <div className="container-fluid d-flex justify-content-center align-items-center" style={{ height: '90vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column flex-md-row bg-gray-50 min-h-screen">
            <CustomNavbar />
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-b">
                    <div>
                        <h2 className="text-blue-700 m-0 fw-bold">Audit Report</h2>
                        <p className="text-secondary m-0 mt-1">Viewing report for: <strong>{auditProfile.user?.name || auditProfile.user?.username}</strong></p>
                    </div>
                    <div>
                        <button
                            className="btn btn-outline-secondary me-3 px-4 shadow-sm"
                            onClick={() => navigate('/audit-users')}
                        >
                            <i className="bi bi-arrow-left me-2"></i>Back to List
                        </button>
                        <button
                            className={`btn ${isSaving ? 'btn-secondary' : 'btn-primary'} px-4 shadow-sm`}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Saving...</>
                            ) : (
                                <><i className="fa-solid fa-floppy-disk me-2"></i>Save Report</>
                            )}
                        </button>
                    </div>
                </div>

                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show shadow-sm`} role="alert">
                        {message.type === 'success' ? <i className="fa-solid fa-circle-check me-2"></i> : <i className="fa-solid fa-triangle-exclamation me-2"></i>}
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
                    </div>
                )}

                <div className="row g-4">
                    {/* Main Editor Section */}
                    <div className="col-lg-8">
                        <div className="card shadow-sm border-0 h-100 h-[70vh]">
                            <div className="card-header bg-white border-b py-3">
                                <h5 className="m-0 text-blue-600 font-semibold"><i className="fa-solid fa-file-lines me-2"></i>Audit Details (Rich Text)</h5>
                                <p className="text-xs text-secondary mb-0 mt-1">Include Current Audit, Previous Audit, and Next Audit Plan.</p>
                            </div>
                            <div className="card-body p-0">
                                <style>{`
                  .ck-editor__editable_inline {
                    min-height: 50vh;
                    border: none !important;
                    border-radius: 0 0 0.375rem 0.375rem !important;
                  }
                  .ck-toolbar {
                    border-left: none !important;
                    border-right: none !important;
                    border-top: none !important;
                    border-radius: 0 !important;
                    background: #f8f9fa !important;
                  }
                `}</style>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={auditProfile.desc || ''}
                                    onChange={handleEditorChange}
                                    config={{
                                        placeholder: 'Document the audit report here...',
                                    }}
                                />
                            </div>
                            <div className="card-footer bg-light border-0 py-2 d-flex justify-content-between text-xs text-secondary">
                                <span>Created: {formatDate(auditProfile.created_at)}</span>
                                <span>Last Updated: {formatDate(auditProfile.updated_at)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Attachments Sidebar */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-white border-b py-3">
                                <h5 className="m-0 text-blue-600 font-semibold"><i className="bi bi-paperclip me-2"></i>Attachments</h5>
                            </div>

                            <div className="card-body">
                                {/* Word Files Section */}
                                <div className="mb-4 bg-gray-50 p-3 rounded border">
                                    <label className="form-label fw-semibold text-secondary mb-2 d-block">Word Documents (.doc, .docx)</label>
                                    
                                    {/* List of currently uploaded Word files */}
                                    <div className="mb-3">
                                        {auditProfile.files?.filter(f => f.file_type === 'doc').map(file => (
                                            <div key={file.id} className="d-flex justify-content-between align-items-center bg-white p-2 rounded mb-2 border-l-4 border-blue-500 shadow-sm">
                                                <div className="text-truncate me-2" style={{ maxWidth: '60%', fontSize: '0.8rem', fontWeight: '500' }}>
                                                    <i className="fa-solid fa-file-word text-blue-600 me-2"></i>
                                                    {file.file.split('/').pop()}
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <a href={removeMediaPrefix(file.file)} target="_blank" rel="noreferrer" className="btn btn-xs btn-primary me-1" title="View/Download">
                                                        <i className="fa-solid fa-eye"></i>
                                                    </a>
                                                    <button onClick={() => handleDeleteFile(file.id)} className="btn btn-xs btn-danger" disabled={isDeleting} title="Delete">
                                                        <i className="fa-solid fa-trash-can"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* New Word files to upload */}
                                    <div className="mb-2">
                                        {uploadFiles.doc_files.map((file, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center bg-blue-50 p-1 px-2 rounded mb-1 border border-blue-100 shadow-sm">
                                                <span className="text-truncate text-xs text-blue-700 fw-medium" style={{ maxWidth: '85%' }}><i className="fa-solid fa-plus-circle me-1"></i>{file.name}</span>
                                                <button onClick={() => removeNewFile('doc_files', idx)} className="btn btn-link p-0 text-red-500 border-0" style={{ fontSize: '14px' }}>
                                                    <i className="fa-solid fa-circle-xmark"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <input
                                        type="file"
                                        className="form-control form-control-sm border-gray-300"
                                        accept=".doc,.docx"
                                        multiple
                                        onChange={(e) => handleFileChange(e, 'doc_files')}
                                    />
                                </div>

                                {/* Excel Files Section */}
                                <div className="mb-2 bg-gray-50 p-3 rounded border">
                                    <label className="form-label fw-semibold text-secondary mb-2 d-block">Spreadsheets (.xls, .xlsx)</label>
                                    
                                    {/* List of currently uploaded Excel files */}
                                    <div className="mb-3">
                                        {auditProfile.files?.filter(f => f.file_type === 'excel').map(file => (
                                            <div key={file.id} className="d-flex justify-content-between align-items-center bg-white p-2 rounded mb-2 border-l-4 border-green-500 shadow-sm">
                                                <div className="text-truncate me-2" style={{ maxWidth: '60%', fontSize: '0.8rem', fontWeight: '500' }}>
                                                    <i className="fa-solid fa-file-excel text-green-600 me-2"></i>
                                                    {file.file.split('/').pop()}
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <a href={removeMediaPrefix(file.file)} target="_blank" rel="noreferrer" className="btn btn-xs btn-primary me-1" title="View/Download">
                                                        <i className="fa-solid fa-eye"></i>
                                                    </a>
                                                    <button onClick={() => handleDeleteFile(file.id)} className="btn btn-xs btn-danger" disabled={isDeleting} title="Delete">
                                                        <i className="fa-solid fa-trash-can"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* New Excel files to upload */}
                                    <div className="mb-2">
                                        {uploadFiles.excel_files.map((file, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center bg-green-50 p-1 px-2 rounded mb-1 border border-green-100 shadow-sm">
                                                <span className="text-truncate text-xs text-green-700 fw-medium" style={{ maxWidth: '85%' }}><i className="fa-solid fa-plus-circle me-1"></i>{file.name}</span>
                                                <button onClick={() => removeNewFile('excel_files', idx)} className="btn btn-link p-0 text-red-500 border-0" style={{ fontSize: '14px' }}>
                                                    <i className="fa-solid fa-circle-xmark"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <input
                                        type="file"
                                        className="form-control form-control-sm border-gray-300"
                                        accept=".xls,.xlsx"
                                        multiple
                                        onChange={(e) => handleFileChange(e, 'excel_files')}
                                    />
                                </div>

                            </div>
                        </div>

                        {/* Audit Update History */}
                        <div className="card shadow-sm border-0 mb-4 overflow-hidden" style={{ maxHeight: '40vh' }}>
                            <div className="card-header bg-white border-b py-3 d-flex justify-content-between align-items-center">
                            <h5 className="m-0 text-blue-600 font-semibold"><i className="fa-solid fa-clock-rotate-left me-2"></i>Update History</h5>
                            <span className="badge bg-blue-100 text-blue-700 rounded-pill">{auditProfile.update_history?.length || 0}</span>
                        </div>
                        <div className="card-body p-0 overflow-y-auto">
                            {auditProfile.update_history && auditProfile.update_history.length > 0 ? (
                                <ul className="list-group list-group-flush">
                                    {auditProfile.update_history.map((history, index) => (
                                        <li key={index} className="list-group-item border-0 py-2 px-3 hover:bg-gray-50 transition-colors">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className="bg-blue-100 text-blue-600 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', fontSize: '10px', fontWeight: 'bold' }}>
                                                        #{auditProfile.update_history.length - index}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="mb-0 text-sm fw-medium text-gray-800">{new Date(history.updated_at).toLocaleDateString()}</p>
                                                    <p className="mb-0 text-xs text-secondary">{new Date(history.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="p-4 text-center">
                                    <i className="fa-solid fa-calendar-alt text-gray-300 display-6 mb-2"></i>
                                    <p className="text-secondary text-sm mb-0">No history available yet.</p>
                                </div>
                            )}
                        </div>
                        </div>

                        <div className="card shadow-sm border-0 bg-blue-50 border-l-4 border-blue-500">
                            <div className="card-body py-3">
                                <h6 className="text-blue-700 fw-bold mb-1"><i className="fa-solid fa-circle-info me-2"></i>Audit Tips</h6>
                                <ul className="text-sm text-secondary ps-3 mb-0">
                                    <li className="mb-1">Always record the <strong>Current Audit</strong> status.</li>
                                    <li className="mb-1">Reference any <strong>Previous Audit</strong> feedback loops.</li>
                                    <li>Draft the <strong>Next Audit</strong> plan at the bottom.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditReport;
